import React, { useEffect, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import {
  Input,
  Select as FormFieldsSelect,
  Toggle,
} from 'components/FormFields';
import useGooglePlaces from 'hooks/useGooglePlaces';

const PacContainerStyles = createGlobalStyle`
  .pac-container {
    background: #1a1a2e;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    margin-top: 4px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    font-family: inherit;
    z-index: 10000;
  }

  .pac-item {
    padding: 8px 12px;
    color: #94a3b8;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    cursor: pointer;
    font-size: 14px;

    &:first-child {
      border-top: none;
    }

    &:hover,
    &.pac-item-selected {
      background: rgba(250, 70, 22, 0.1);
    }
  }

  .pac-item-query {
    color: #ffffff;
    font-size: 14px;
  }

  .pac-icon {
    display: none;
  }

  .pac-matched {
    color: #fa4616;
  }
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;

  /* Override Input component styles for dark theme */
  label {
    color: #94a3b8 !important;
    font-size: 13px !important;
    font-weight: 500 !important;
  }

  input,
  textarea,
  select {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    color: #ffffff !important;

    &::placeholder {
      color: #64748b !important;
    }

    &:focus {
      border-color: #fa4616 !important;
      box-shadow: 0 0 0 1px #fa4616 !important;
    }
  }

  /* Description text styling */
  p {
    color: #94a3b8 !important;
    font-size: 12px !important;
  }
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  /* Ensure dark theme overrides apply to inputs in GridRow too */
  label {
    color: #94a3b8 !important;
    font-size: 13px !important;
    font-weight: 500 !important;
  }

  input,
  textarea,
  select {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    color: #ffffff !important;

    &::placeholder {
      color: #64748b !important;
    }

    &:focus {
      border-color: #fa4616 !important;
      box-shadow: 0 0 0 1px #fa4616 !important;
    }
  }

  /* Description text styling */
  p {
    color: #94a3b8 !important;
    font-size: 12px !important;
  }
`;

const ToggleWrapper = styled.div`
  margin-bottom: 16px;
`;

// Names keyed by USPS code; object literals preserve insertion order, so the
// rendered options stay alphabetical. Select renders its own placeholder, but
// this list keeps an explicit empty entry as the initial value.
const US_STATE_NAMES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export const US_STATES = [
  { label: '-- Select State --', value: '' },
  ...Object.entries(US_STATE_NAMES).map((entry) => ({
    label: entry[1],
    value: entry[0],
  })),
];

const COUNTRIES = [
  { label: 'United States', value: 'US' },
  { label: 'Canada', value: 'CA' },
];

const DEFAULT_COUNTRY = 'US';

const AddressForm = ({
  address,
  onChange,
  onAddressSelect,
  showSameAsCheckbox = false,
  sameAsChecked = false,
  onSameAsToggle,
  errors = {},
  idPrefix = '',
}) => {
  const { initAutocomplete } = useGooglePlaces();
  const streetInputRef = useRef(null);
  const onAddressSelectRef = useRef(onAddressSelect);
  const onChangeRef = useRef(onChange);

  // Keep refs current to avoid stale closures in the Google Places callback
  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
    onChangeRef.current = onChange;
  });

  // The country select falls back to DEFAULT_COUNTRY for display, but a
  // default the form never stores is a lie: country is required by the signup
  // mutation, and it stays unset whenever the address is typed by hand rather
  // than picked from Places autocomplete (which does parse a country). Commit
  // the value the user is already being shown.
  useEffect(() => {
    if (!address.country) {
      onChangeRef.current('country', DEFAULT_COUNTRY);
    }
  }, [address.country]);

  useEffect(() => {
    if (!sameAsChecked && streetInputRef.current) {
      const inputEl =
        streetInputRef.current.querySelector('input') || streetInputRef.current;

      initAutocomplete(inputEl, (parsed) => {
        if (onAddressSelectRef.current) {
          onAddressSelectRef.current(parsed);
        } else {
          onChangeRef.current('street1', parsed.street1);
          onChangeRef.current('city', parsed.city);
          onChangeRef.current('state', parsed.state);
          onChangeRef.current('zipcode', parsed.zipcode);
          onChangeRef.current('country', parsed.country);
        }
      });
    }
  }, [sameAsChecked, initAutocomplete]);

  return (
    <>
      <PacContainerStyles />
      {showSameAsCheckbox && (
        <ToggleWrapper>
          <Toggle
            checked={sameAsChecked}
            onChange={(e) => onSameAsToggle(e.target.checked)}
            label="Same as previous address"
          />
        </ToggleWrapper>
      )}

      {!sameAsChecked && (
        <FormGrid>
          <div ref={streetInputRef}>
            <Input
              id={`${idPrefix}street1`}
              label="Street Address"
              placeholder="123 Main Street"
              value={address.street1 || ''}
              onChange={(e) => onChange('street1', e.target.value)}
              error={errors.street1}
              required
              autoComplete="nofill"
            />
          </div>

          <Input
            id={`${idPrefix}street2`}
            label="Apt / Suite (Optional)"
            placeholder="Suite 100"
            value={address.street2 || ''}
            onChange={(e) => onChange('street2', e.target.value)}
            autoComplete="nofill"
          />

          <GridRow>
            <Input
              id={`${idPrefix}city`}
              label="City"
              placeholder="Los Angeles"
              value={address.city || ''}
              onChange={(e) => onChange('city', e.target.value)}
              error={errors.city}
              required
              autoComplete="nofill"
            />

            <FormFieldsSelect
              label="State"
              placeholder="-- Select state --"
              value={address.state || ''}
              onChange={(e) => onChange('state', e.target.value)}
              options={US_STATES}
              error={errors.state}
              width="100%"
              required
            />
          </GridRow>

          <GridRow>
            <Input
              id={`${idPrefix}zipcode`}
              label="Zip Code"
              placeholder="90210"
              value={address.zipcode || ''}
              onChange={(e) => onChange('zipcode', e.target.value)}
              error={errors.zipcode}
              required
              autoComplete="nofill"
            />

            <FormFieldsSelect
              label="Country"
              placeholder="-- Select country --"
              value={address.country || DEFAULT_COUNTRY}
              onChange={(e) => onChange('country', e.target.value)}
              options={COUNTRIES}
              width="100%"
              required
            />
          </GridRow>
        </FormGrid>
      )}
    </>
  );
};

export default AddressForm;
